import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Spin, Tabs, Typography, message } from "antd";
import { publicGetApi } from "../../../utils/apiHelper";
import { useDispatch } from "react-redux";
import { acceptInvite } from "../../../redux/actions/organizationAction";
import FormLogin from "../../../components/forms/formLogin";
import FormSignup from "../../../components/forms/formSignup";
import { isPublicEmailDomain } from "../../../utils/workEmail";

function getLoggedInEmail() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return (user.emailAddress || "").trim().toLowerCase();
  } catch {
    return "";
  }
}

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [authTab, setAuthTab] = useState("signup");
  const [loggedInEmail, setLoggedInEmail] = useState(getLoggedInEmail);
  const isLoggedIn = !!localStorage.getItem("accessToken");

  useEffect(() => {
    publicGetApi(`/invites/${token}`)
      .then((response) => {
        if (response.status === 200) {
          setInvite(response.data.result.invite);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const completeAccept = useCallback(() => {
    setAccepting(true);
    return dispatch(acceptInvite(token))
      .then(() => {
        message.success("Invite accepted.");
        navigate("/app");
        window.location.reload();
      })
      .catch(() => {
        message.error("Could not accept invite. Please try again.");
      })
      .finally(() => setAccepting(false));
  }, [dispatch, navigate, token]);

  const handleAccept = () => {
    if (!isLoggedIn) {
      return;
    }
    completeAccept();
  };

  const handleAuthenticated = () => {
    setLoggedInEmail(getLoggedInEmail());
    return completeAccept();
  };

  const handleSignOut = () => {
    localStorage.clear();
    setLoggedInEmail("");
    window.location.reload();
  };

  const handleAccountNotFound = () => {
    setAuthTab("signup");
    message.info(
      "No account exists for this email yet. Use Create account to join.",
      6,
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin />
      </div>
    );
  }

  if (!invite) {
    return (
      <div style={{ padding: 48, maxWidth: 480, margin: "0 auto" }}>
        <Card>
          <Typography.Title level={4}>Invalid invite</Typography.Title>
          <Typography.Paragraph>
            This invite link is invalid or has expired.
          </Typography.Paragraph>
        </Card>
      </div>
    );
  }

  if (isPublicEmailDomain(invite.email)) {
    return (
      <div style={{ padding: 48, maxWidth: 520, margin: "0 auto" }}>
        <Card>
          <Typography.Title level={4}>Invite not available</Typography.Title>
          <Typography.Paragraph>
            This invite was sent to a public email domain ({invite.email}).
            Mailinflow requires a work email address for sign-in and sign-up.
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            Ask your organization admin to send a new invite to your work email.
          </Typography.Paragraph>
        </Card>
      </div>
    );
  }

  const inviteEmail = invite.email.trim().toLowerCase();
  const emailMatches =
    isLoggedIn && loggedInEmail && loggedInEmail === inviteEmail;

  return (
    <div style={{ padding: 48, maxWidth: 560, margin: "0 auto" }}>
      <Card>
        <Typography.Title level={4}>Organization invite</Typography.Title>
        <Typography.Paragraph>
          You have been invited to join{" "}
          <strong>{invite.organizationName}</strong> as{" "}
          <strong>{invite.role}</strong>.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          Invite sent to {invite.email}. Expires{" "}
          {new Date(invite.expiresAt).toLocaleDateString()}.
        </Typography.Paragraph>

        {isLoggedIn && !emailMatches ? (
          <>
            <Typography.Paragraph type="warning">
              You are signed in as {loggedInEmail || "another account"}. Sign
              out and use {invite.email} to accept this invite.
            </Typography.Paragraph>
            <Button onClick={handleSignOut}>Sign out</Button>
          </>
        ) : null}

        {isLoggedIn && emailMatches ? (
          <Button type="primary" loading={accepting} onClick={handleAccept}>
            Accept invite
          </Button>
        ) : null}

        {!isLoggedIn ? (
          <Tabs
            activeKey={authTab}
            onChange={setAuthTab}
            items={[
              {
                key: "signup",
                label: "Create account",
                children: (
                  <FormSignup
                    compact
                    lockedEmail={invite.email}
                    onAuthenticated={handleAuthenticated}
                  />
                ),
              },
              {
                key: "signin",
                label: "Sign in",
                children: (
                  <>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                      Use this only if you already have a Mailinflow account with{" "}
                      {invite.email}.
                    </Typography.Paragraph>
                    <FormLogin
                      compact
                      lockedEmail={invite.email}
                      onAuthenticated={handleAuthenticated}
                      onAccountNotFound={handleAccountNotFound}
                    />
                  </>
                ),
              },
            ]}
          />
        ) : null}
      </Card>
    </div>
  );
}
